import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
	ActionIcon,
	Badge,
	Box,
	Button,
	Chip,
	Collapse,
	createStyles,
	Flex,
	Grid,
	Group,
	Loader,
	NumberInput,
	Popover,
	Progress,
	ScrollArea,
	Select,
	Stack,
	Switch,
	Text,
	Tooltip,
	UnstyledButton,
	useMantineTheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconChevronRight, IconFilter } from '@tabler/icons-react';
import { useLocation } from 'react-router-dom';
import { makeSafeRequest } from '../../common/makeSafeRequest';

interface ISimulationPage {}

type SimulationProgressResponse = {
	progress: SimulationProgress[];
};

type SimulationProgress = {
	runnum: number;
	status: string;
	progress: number;
};

type ScenarioOption = {
	id: number;
	name: string;
	project_name: string | null;
};

type ScenariosResponse = {
	scenarios: ScenarioOption[];
};

type CesiumLinksResponse = {
	links: Record<string, string | null>;
};

const SENSOR_SERVER_LOG_LEVEL_OPTIONS = [
	{ value: 'DEBUG', label: 'DEBUG' },
	{ value: 'INFO', label: 'INFO' },
	{ value: 'WARN', label: 'WARN' },
	{ value: 'ERROR', label: 'ERROR' },
];

const CESIUM_LINKS_STORAGE_KEY = 'simulation.cesiumLinks';

const loadCachedCesiumLinks = (): Record<number, string | null> => {
	try {
		return JSON.parse(localStorage.getItem(CESIUM_LINKS_STORAGE_KEY) ?? '{}');
	} catch {
		return {};
	}
};

const LOOKBACK_OPTIONS = [
	{ value: '15', label: '15 mins' },
	{ value: '60', label: '1 hour' },
	{ value: '120', label: '2 hours' },
	{ value: '720', label: '12 hours' },
	{ value: '1440', label: '24 hours' },
];

const statusColor = (status: string) => {
	switch (status.toLowerCase()) {
		case 'running':
			return 'blue';
		case 'finished':
			return 'green';
		case 'failed':
			return 'red';
		case 'pending':
			return 'yellow';
		default:
			return 'gray';
	}
};

const SimulationPage: React.FC<ISimulationPage> = () => {
	const { classes } = useStyles();
	const location: { state: { scenarioId?: string } } = useLocation();
	const theme = useMantineTheme();

	const [scenarioId, setScenarioId] = useState(location?.state?.scenarioId || '');
	const [duration, setDuration] = useState<number>(12);
	const [memValue, setMemValue] = useState<number>(25);
	const [memUnit, setMemUnit] = useState<string>('Gi');
	const [cpuLimit, setCpuLimit] = useState<number>(2000);
	const [parallelism, setParallelism] = useState<number>(1);
	const [completions, setCompletions] = useState<number>(1);
	const [sensorServerEnabled, setSensorServerEnabled] = useState<boolean>(true);
	const [sensorServerNumSims, setSensorServerNumSims] = useState<number>(4);
	const [sensorServerThreadsPerSim, setSensorServerThreadsPerSim] = useState<number>(2);
	const [sensorServerLogLevel, setSensorServerLogLevel] = useState<string>('INFO');

	const [progressData, setProgressData] = useState<SimulationProgress[]>();
	const [scenarioOptions, setScenarioOptions] = useState<{ value: string; label: string }[]>([]);
	const [isFetchingProgress, setIsFetchingProgress] = useState(false);
	const [statusFilter, setStatusFilter] = useState<string | null>(null);
	const [filterOpen, setFilterOpen] = useState(false);
	const [lookbackMinutes, setLookbackMinutes] = useState<string>('15');
	const [cesiumLinks, setCesiumLinks] =
		useState<Record<number, string | null>>(loadCachedCesiumLinks);
	const [advancedOpen, setAdvancedOpen] = useState(false);
	const [isStoppingSimulations, setIsStoppingSimulations] = useState(false);

	const filteredProgressData = statusFilter
		? progressData?.filter((sim) => sim.status.toLowerCase() === statusFilter)
		: progressData;

	const fetchProgress = async (lookback: string = lookbackMinutes) => {
		setIsFetchingProgress(true);
		await makeSafeRequest('simulation/progress', 'GET', {
			params: { lookback_minutes: lookback },
			onErrorMessage: 'Failed to fetch simulation progress. Please try again.',
			onSuccess: (data: SimulationProgressResponse) => {
				setProgressData(data.progress);
				setIsFetchingProgress(false);
			},
		});
	};

	const fetchCesiumLinks = async (runnums: number[]) => {
		await makeSafeRequest('simulation/cesium-links', 'GET', {
			params: { runnums: runnums.join(',') },
			onErrorMessage: 'Failed to fetch Cesium replay links.',
			onSuccess: (data: CesiumLinksResponse) => {
				setCesiumLinks((prev) => {
					const next = { ...prev };
					Object.entries(data.links).forEach(([runnum, path]) => {
						next[Number(runnum)] = path;
					});
					localStorage.setItem(CESIUM_LINKS_STORAGE_KEY, JSON.stringify(next));
					return next;
				});
			},
		});
	};

	const fetchScenarios = async () => {
		await makeSafeRequest('scenarios/all/', 'GET', {
			onErrorMessage: 'Failed to fetch scenarios. Please try again.',
			onSuccess: (data: ScenariosResponse) => {
				setScenarioOptions(
					data.scenarios.map((scenario) => ({
						value: String(scenario.id),
						label: `#${scenario.id} · ${scenario.name}${
							scenario.project_name ? ` — ${scenario.project_name}` : ''
						}`,
					}))
				);
			},
		});
	};

	const stopSimulations = async () => {
		if (!window.confirm('This will stop every currently running simulation. Are you sure?')) {
			return;
		}

		setIsStoppingSimulations(true);
		await makeSafeRequest('simulation/stop', 'POST', {
			onSuccessMessage: 'Stop requested for all running simulations.',
			onErrorMessage: 'Failed to stop simulations. Please try again.',
		});
		setIsStoppingSimulations(false);
		fetchProgress();
	};

	const scheduleSimulation = async () => {
		if (!scenarioId) {
			notifications.show({
				title: 'Validation Error',
				message: 'Scenario ID is required to schedule a simulation.',
				color: 'red',
			});
			return;
		}

		const response = await makeSafeRequest('simulation/job', 'POST', {
			payload: {
				scenario_id: String(scenarioId),
				duration: Number(duration),
				mem_value: Number(memValue),
				mem_unit: String(memUnit),
				cpu_value: Number(cpuLimit),
				parallelism: Number(parallelism),
				completions: Number(completions),
				sensor_server_enabled: Boolean(sensorServerEnabled),
				sensor_server_num_sims: Number(sensorServerNumSims),
				sensor_server_threads_per_sim: Number(sensorServerThreadsPerSim),
				sensor_server_log_level: String(sensorServerLogLevel),
			},
			onSuccessMessage: `Simulation for scenario ${scenarioId} scheduled successfully!`,
			onErrorMessage: `Failed to schedule simulation for scenario ${scenarioId}. Please try again.`,
		});
		console.log('Schedule Simulation Response:', response);
	};

	useEffect(() => {
		fetchScenarios();
	}, []);

	useEffect(() => {
		setIsFetchingProgress(true);
		fetchProgress(lookbackMinutes).then(() => setIsFetchingProgress(false));

		const interval = setInterval(() => {
			fetchProgress(lookbackMinutes);
		}, 10000); // Refresh every 10 seconds

		return () => clearInterval(interval);
	}, [lookbackMinutes]);

	useEffect(() => {
		const finishedRunnums = Array.from(
			new Set(
				(progressData ?? [])
					.filter((sim) => sim.status.toLowerCase() === 'finished' && sim.runnum > 0)
					.map((sim) => sim.runnum)
			)
		);
		const missingRunnums = finishedRunnums.filter((runnum) => !(runnum in cesiumLinks));

		if (missingRunnums.length > 0) {
			fetchCesiumLinks(missingRunnums);
		}
	}, [progressData]);

	return (
		<Grid
			columns={2}
			style={{
				height: 'calc(100vh - var(--header-height) - var(--footer-height) - var(--content-padding) * 2 - var(--breadcrumbs-height))',
			}}
		>
			<Grid.Col span={1} style={{ borderRight: '1px solid gray' }}>
				<Flex
					direction="column"
					align="center"
					gap="md"
					style={{ height: '100%', width: '100%', padding: '2rem' }}
				>
					<h1 style={{ margin: 0, alignSelf: 'flex-start' }}>Scheduling</h1>
					<Select
						label="Scenario"
						value={scenarioId}
						onChange={(val) => setScenarioId(val ?? '')}
						data={scenarioOptions}
						placeholder="Select a scenario"
						nothingFound="No scenarios found"
						searchable
						clearable
						style={{ width: '100%' }}
						required
					/>
					<NumberInput
						label="Duration (h)"
						value={duration}
						onChange={(val) => setDuration(Number(val))}
						min={1}
						placeholder="Enter duration in hours"
						style={{ width: '100%' }}
					/>
					<UnstyledButton
						onClick={() => setAdvancedOpen((o) => !o)}
						style={{ width: '100%', marginTop: '0.5rem' }}
					>
						<Group spacing={4}>
							<IconChevronRight
								size={16}
								style={{
									transform: advancedOpen ? 'rotate(90deg)' : 'none',
									transition: 'transform 150ms ease',
								}}
								className={classes.switchLabel}
							/>
							<Text size="sm" weight={600} className={classes.switchLabel}>
								Advanced settings
							</Text>
						</Group>
					</UnstyledButton>
					<Box style={{ width: '100%', alignSelf: 'stretch' }}>
						<Collapse in={advancedOpen}>
							<Stack spacing="md" style={{ width: '100%' }}>
								<Group spacing="sm" style={{ width: '100%' }}>
									<NumberInput
										label="Process memory limit"
										value={memValue}
										onChange={(val) => setMemValue(Number(val))}
										min={1}
										style={{ flex: 1 }}
									/>
									<Select
										label="Unit"
										value={memUnit}
										onChange={(val) => setMemUnit(val ?? 'Gi')}
										data={[
											{ value: 'Gi', label: 'GiB (Gi)' },
											{ value: 'Mi', label: 'MiB (Mi)' },
										]}
										style={{ flex: 0.5 }}
									/>
								</Group>
								<NumberInput
									label="CPU Limit"
									description="Set CPU limit in millicores (e.g., 500m for 0.5 CPU)"
									value={cpuLimit}
									onChange={(val) => setCpuLimit(Number(val))}
									min={1}
									max={16000}
									sx={{ '.mantine-InputWrapper-description': { opacity: 0.5 } }}
									style={{ width: '100%' }}
								/>
								<NumberInput
									label="Parallelism"
									value={parallelism}
									onChange={(val) => setParallelism(Number(val))}
									min={1}
									max={completions}
									style={{ width: '100%' }}
								/>

								<NumberInput
									label="Completions"
									value={completions}
									onChange={(val) => setCompletions(Number(val))}
									min={1}
									style={{ width: '100%' }}
								/>

								<Switch
									label="Sensor Server"
									checked={sensorServerEnabled}
									onChange={(event) =>
										setSensorServerEnabled(event.currentTarget.checked)
									}
									classNames={{ label: classes.switchLabel }}
									style={{ width: '100%', marginTop: '0.5rem' }}
								/>
								<Group spacing="sm" style={{ width: '100%' }}>
									<NumberInput
										label="Sensor Server Sims"
										value={sensorServerNumSims}
										onChange={(val) => setSensorServerNumSims(Number(val))}
										min={1}
										disabled={!sensorServerEnabled}
										style={{ flex: 1 }}
									/>
									<NumberInput
										label="Threads / Sim"
										value={sensorServerThreadsPerSim}
										onChange={(val) =>
											setSensorServerThreadsPerSim(Number(val))
										}
										min={1}
										disabled={!sensorServerEnabled}
										style={{ flex: 1 }}
									/>
									<Select
										label="Log Level"
										value={sensorServerLogLevel}
										onChange={(val) => setSensorServerLogLevel(val ?? 'INFO')}
										data={SENSOR_SERVER_LOG_LEVEL_OPTIONS}
										disabled={!sensorServerEnabled}
										style={{ flex: 1 }}
									/>
								</Group>
							</Stack>
						</Collapse>
					</Box>

					<Button onClick={scheduleSimulation} style={{ marginTop: '1rem' }}>
						Schedule Simulation
					</Button>
				</Flex>
			</Grid.Col>
			<Grid.Col span={1} style={{ height: '100%', overflow: 'hidden' }}>
				<Flex
					direction="column"
					align="center"
					style={{ height: '100%', width: '100%', padding: '2rem' }}
				>
					<Group
						position="apart"
						style={{ width: '100%', marginBottom: '1rem', flexGrow: 0 }}
					>
						<h1 style={{ margin: 0 }}>Progress</h1>
						<Flex style={{ alignItems: 'center', gap: '1rem' }}>
							{isFetchingProgress && <Loader size="md" right={0} />}
							<Popover
								opened={filterOpen}
								onClose={() => setFilterOpen(false)}
								position="bottom-end"
								withArrow
								shadow="md"
							>
								<Popover.Target>
									<Tooltip
										label={
											statusFilter
												? `Filter: ${statusFilter}`
												: 'Filter by status'
										}
									>
										<ActionIcon
											variant={statusFilter ? 'filled' : 'subtle'}
											color={statusFilter ? theme.primaryColor : 'gray'}
											size="lg"
											onClick={() => setFilterOpen((o) => !o)}
										>
											<IconFilter size={18} />
										</ActionIcon>
									</Tooltip>
								</Popover.Target>
								<Popover.Dropdown>
									<Stack spacing={6}>
										<Text size="xs" weight={600} color="dimmed">
											Filter by status
										</Text>
										<Chip.Group
											value={statusFilter ?? ''}
											onChange={(v) => {
												setStatusFilter(
													(Array.isArray(v) ? v[0] : v) || null
												);
												setFilterOpen(false);
											}}
										>
											<CustomChip value="">All</CustomChip>
											<CustomChip value="pending">Pending</CustomChip>
											<CustomChip value="running">Running</CustomChip>
											<CustomChip value="finished">Finished</CustomChip>
											<CustomChip value="failed">Failed</CustomChip>
										</Chip.Group>
									</Stack>
								</Popover.Dropdown>
							</Popover>

							<Select
								value={lookbackMinutes}
								onChange={(val) => setLookbackMinutes(val ?? '15')}
								data={LOOKBACK_OPTIONS}
								style={{ width: 120 }}
							/>

							<Button onClick={() => fetchProgress()}>Refresh</Button>
							<Button
								color="accentRed"
								loading={isStoppingSimulations}
								onClick={stopSimulations}
							>
								Stop Simulations
							</Button>
						</Flex>
					</Group>
					<Box style={{ width: '100%', flexGrow: 1, minHeight: 0 }}>
						<ScrollArea style={{ width: '100%', height: '100%' }}>
							<Stack spacing="sm">
								{!filteredProgressData || filteredProgressData.length === 0 ? (
									<Text color="dimmed" align="center">
										{statusFilter
											? 'No simulations match the selected status.'
											: 'No simulation data available.'}
									</Text>
								) : (
									filteredProgressData.map((sim, idx) => (
										<Flex
											key={sim.runnum ? `run-${sim.runnum}` : `init-${idx}`}
											direction="column"
											gap={4}
											style={{
												border: '1px solid #ccc',
												borderRadius: 8,
												padding: '0.75rem',
											}}
										>
											<Group position="apart">
												<Text className={classes.runTitle}>
													Run{' '}
													{sim.runnum ? sim.runnum : 'initializing...'}
												</Text>
												<Group spacing="xs">
													{sim.status.toLowerCase() === 'finished' &&
														cesiumLinks[sim.runnum] && (
															<Button
																component="a"
																href={
																	cesiumLinks[
																		sim.runnum
																	] as string
																}
																target="_blank"
																rel="noopener noreferrer"
																size="xs"
																variant="light"
															>
																Replay
															</Button>
														)}
													<Badge color={statusColor(sim.status)}>
														{sim.status}
													</Badge>
												</Group>
											</Group>
											<Progress
												value={sim.progress * 100}
												size="lg"
												color={theme.primaryColor}
												animate={sim.status.toLowerCase() === 'running'}
											/>
											<Text size="xs" color="dimmed" align="right">
												{Math.floor(sim.progress * 100)}%
											</Text>
										</Flex>
									))
								)}
							</Stack>
						</ScrollArea>
					</Box>
				</Flex>
			</Grid.Col>
		</Grid>
	);
};

const useStyles = createStyles((theme) => ({
	runTitle: {
		color: `${theme.colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.light[2]} !important`,
		fontWeight: 'bold',
	},
	switchLabel: {
		color: `${theme.colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.light[2]} !important`,
	},
}));

const CustomChip = styled(Chip)`
	.mantine-Chip-label {
		color: ${({ theme }) =>
			theme.colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.light[3]};
	}
`;

export default SimulationPage;
